import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  article?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  twitterHandle?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'David Sheeshka | Web Development & Digital Services',
  description = 'Professional web development, cloud solutions, and digital services. Specializing in React, AWS, and custom software development to help your business thrive.',
  keywords = 'web development, cloud computing, AI solutions, digital services, React development, AWS, software development',
  image = '/images/og-image.jpg', // Default Open Graph image
  url = typeof window !== 'undefined' ? window.location.href : 'https://davidsheeshka.com',
  type = 'website',
  article = false,
  publishedTime,
  modifiedTime,
  author = 'David Sheeshka',
  twitterHandle = '@davidsheeshka', // Replace with your Twitter handle
}) => {
  // Truncate description to optimal SEO length
  const truncatedDescription = description.length > 160 
    ? description.substring(0, 157) + '...' 
    : description;

  // Canonical URL to prevent duplicate content issues
  const canonicalUrl = url?.endsWith('/') ? url.slice(0, -1) : url;

  return (
    <Helmet>
      {/* Basic Metadata */}
      <title>{title}</title>
      <meta name="description" content={truncatedDescription} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? 'article' : type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image.startsWith('http') ? image : `https://davidsheeshka.com${image}`} />
      <meta property="og:site_name" content="David Sheeshka" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={image.startsWith('http') ? image : `https://davidsheeshka.com${image}`} />
      
      {/* Article Specific (if applicable) */}
      {article && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {article && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {article && author && <meta property="article:author" content={author} />}
      
      {/* Additional Metadata for Search Engines */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content={author} />
    </Helmet>
  );
};

export default SEO;
